import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getNotebookService } from '../../services/NotebookService';
import { getApiUrl } from '../../utils/api';
import { FileTreeRow, DirChevron } from './FileTreeRow';
import type { FileItem } from './FileTreeRow';

interface FileExplorerProps {
  sessionId: string;
  token: string;
  onFileSelect?: (file: FileItem) => void;
  onFileCreate?: (path: string, type: 'notebook' | 'file' | 'directory') => void;
  className?: string;
  /** Increment to force a refresh (e.g., after copying a notebook to my_data/) */
  refreshTrigger?: number;
}

type SortMode = 'name' | 'date' | 'size' | 'type';

const sortFiles = (items: FileItem[], mode: SortMode = 'name'): FileItem[] => {
  return [...items].sort((a, b) => {
    // Directories always first
    if (a.type === 'directory' && b.type !== 'directory') return -1;
    if (a.type !== 'directory' && b.type === 'directory') return 1;
    switch (mode) {
      case 'date':
        return (b.modified || '').localeCompare(a.modified || ''); // newest first
      case 'size':
        return (b.size || 0) - (a.size || 0); // largest first
      case 'type': {
        const extA = a.name.includes('.') ? a.name.split('.').pop()! : '';
        const extB = b.name.includes('.') ? b.name.split('.').pop()! : '';
        return extA.localeCompare(extB) || a.name.localeCompare(b.name);
      }
      default:
        return a.name.localeCompare(b.name);
    }
  });
};

const parseContents = (contents: any, mode: SortMode = 'name'): FileItem[] => {
  if (!contents?.content) return [];
  return sortFiles(
    contents.content.map((item: any) => ({
      name: item.name,
      path: item.path,
      type: item.type === 'directory' ? 'directory' : item.type === 'notebook' ? 'notebook' : 'file',
      size: item.size,
      created: item.created,
      modified: item.last_modified,
    })),
    mode
  );
};

export const FileExplorer: React.FC<FileExplorerProps> = ({
  sessionId,
  token,
  onFileSelect,
  onFileCreate,
  className = '',
  refreshTrigger,
}) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [pathHistory, setPathHistory] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [newItemType, setNewItemType] = useState<'notebook' | 'file' | 'directory'>('notebook');
  const [newItemName, setNewItemName] = useState('');
  const [showGitCloneDialog, setShowGitCloneDialog] = useState(false);
  const [gitRepoUrl, setGitRepoUrl] = useState('');
  const [gitTargetDir, setGitTargetDir] = useState('');
  const [gitCloneLoading, setGitCloneLoading] = useState(false);
  const [gitCloneError, setGitCloneError] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<Array<{ name: string; status: 'pending' | 'uploading' | 'done' | 'error'; error?: string }>>([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; file: FileItem } | null>(null);
  const [renamingFile, setRenamingFile] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmFile, setDeleteConfirmFile] = useState<string | null>(null);
  // Drag-and-drop state
  const [draggedFile, setDraggedFile] = useState<FileItem | null>(null);
  const [dropTargetDir, setDropTargetDir] = useState<string | null>(null);
  const dropTargetRef = useRef<string | null>(null);
  // Tree state
  const [expandedDirs, setExpandedDirs] = useState<Record<string, FileItem[]>>({});
  const [loadingDirs, setLoadingDirs] = useState<Set<string>>(new Set());
  // File search (debounced: searchInput for the input, debouncedQuery for filtering)
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Sort mode
  const [sortMode, setSortMode] = useState<SortMode>('name');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const gitTargetDirManualRef = useRef(false);
  const fileListRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const renameInitTimestamp = useRef<number>(0);
  const autoExpandedRef = useRef(false);
  const lastClickedPathRef = useRef<string | null>(null);
  // Tracks how many items to render per directory (for large dir virtualization)
  const [dirRenderLimits, setDirRenderLimits] = useState<Record<string, number>>({});
  // Recently opened files
  const [recentFiles, setRecentFiles] = useState<Array<{ path: string; name: string; timestamp: number }>>([]);
  const [recentCollapsed, setRecentCollapsed] = useState(false);

  const notebookService = getNotebookService({ sessionId, token });

  const RECENT_FILES_KEY = `propalgos-recent-files-${sessionId}`;

  // Load recent files from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_FILES_KEY);
      if (raw) setRecentFiles(JSON.parse(raw));
    } catch { /* ignore corrupt data */ }
  }, [RECENT_FILES_KEY]);

  // Track recently opened files
  const trackRecentFile = useCallback((file: FileItem) => {
    if (file.type === 'directory') return;
    setRecentFiles(prev => {
      const filtered = prev.filter(r => r.path !== file.path);
      const updated = [{ path: file.path, name: file.name, timestamp: Date.now() }, ...filtered].slice(0, 10);
      try { localStorage.setItem(RECENT_FILES_KEY, JSON.stringify(updated)); } catch { /* quota */ }
      return updated;
    });
  }, [RECENT_FILES_KEY]);

  const isReadOnlyPath = (path: string) => path === 'samples' || path.startsWith('samples/');

  // One-time initialization on mount
  useEffect(() => {
    notebookService.initialize().catch((err) => {
      console.error('Failed to initialize notebook service:', err);
    });
  }, [sessionId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFiles = useCallback(async (path: string = '') => {
    setIsLoading(true);
    setError(null);

    const RETRIES = 3;
    const BACKOFF = [1000, 2000, 4000];
    for (let i = 0; i < RETRIES; i++) {
      try {
        const contents = await notebookService.listFiles(path);
        setFiles(parseContents(contents));
        setIsLoading(false);
        return;
      } catch (err) {
        if (i < RETRIES - 1) {
          await new Promise(r => setTimeout(r, BACKOFF[i]));
        } else {
          console.error('Failed to load files after retries:', err);
          setError('Failed to load files');
          setIsLoading(false);
        }
      }
    }
  }, [notebookService]);

  // Load root files when currentPath changes
  useEffect(() => {
    loadFiles(currentPath);
  }, [currentPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload contents of all currently-expanded directories without collapsing them.
  const reloadExpandedDirs = useCallback(async (dirs: Record<string, FileItem[]>) => {
    const paths = Object.keys(dirs);
    if (paths.length === 0) return;

    const updated: Record<string, FileItem[]> = {};
    await Promise.all(
      paths.map(async (dirPath) => {
        try {
          const contents = await notebookService.listFiles(dirPath);
          updated[dirPath] = parseContents(contents, sortMode);
        } catch {
          // Directory may have been deleted — omit from result to collapse it
        }
      })
    );
    setExpandedDirs(updated);
  }, [notebookService, sortMode]);

  // External refresh trigger (e.g., after notebook copy to my_data/)
  // Reloads root + expanded dirs in-place so tree structure is preserved.
  useEffect(() => {
    if (refreshTrigger === undefined || refreshTrigger === 0) return;
    loadFiles(currentPath);
    // Snapshot current expanded dirs before the async reload
    setExpandedDirs(prev => {
      reloadExpandedDirs(prev);
      return prev; // keep current until async update replaces it
    });
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sort when sort mode changes
  useEffect(() => {
    setFiles(prev => sortFiles([...prev], sortMode));
    setExpandedDirs(prev => {
      const next: Record<string, FileItem[]> = {};
      for (const [k, v] of Object.entries(prev)) {
        next[k] = sortFiles([...v], sortMode);
      }
      return next;
    });
  }, [sortMode]);

  // Debounce search input — keeps typing responsive while coalescing filter computation
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchInput), 150);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Refresh: reload root + expanded dirs in-place (preserves tree structure)
  const handleRefresh = useCallback(() => {
    setLoadingDirs(new Set());
    loadFiles(currentPath);
    setExpandedDirs(prev => {
      reloadExpandedDirs(prev);
      return prev;
    });
  }, [currentPath, loadFiles, reloadExpandedDirs]);

  // Targeted refresh: reload only the affected directory and its parent
  const refreshDirectory = useCallback(async (affectedPath: string) => {
    const parentDir = affectedPath.substring(0, affectedPath.lastIndexOf('/')) || '';

    // Reload root if the affected path is at root level
    if (parentDir === '' || parentDir === currentPath) {
      const contents = await notebookService.listFiles(currentPath);
      setFiles(parseContents(contents, sortMode));
    }

    // Reload the specific expanded directory containing the affected file
    setExpandedDirs(prev => {
      if (prev[parentDir] === undefined) return prev;
      // Async reload just this directory
      notebookService.listFiles(parentDir).then(contents => {
        setExpandedDirs(p => ({ ...p, [parentDir]: parseContents(contents, sortMode) }));
      }).catch(() => {
        // Directory may have been deleted — remove from expanded
        setExpandedDirs(p => {
          const next = { ...p };
          delete next[parentDir];
          return next;
        });
      });
      return prev;
    });
  }, [currentPath, notebookService, sortMode]);

  const navigateTo = (path: string) => {
    if (path !== currentPath) {
      setPathHistory(prev => [...prev, currentPath]);
      setCurrentPath(path);
      setExpandedDirs({});
      setLoadingDirs(new Set());
    }
  };

  const navigateBack = () => {
    if (pathHistory.length > 0) {
      const previousPath = pathHistory[pathHistory.length - 1];
      setPathHistory(prev => prev.slice(0, -1));
      setCurrentPath(previousPath);
      setExpandedDirs({});
      setLoadingDirs(new Set());
    }
  };

  const navigateToRoot = () => {
    if (currentPath !== '') {
      setPathHistory([]);
      setCurrentPath('');
      setExpandedDirs({});
      setLoadingDirs(new Set());
    }
  };

  // Toggle expand/collapse for a directory (single-click)
  const toggleDir = useCallback(async (dirPath: string) => {
    // If already expanded, collapse
    if (expandedDirs[dirPath] !== undefined) {
      setExpandedDirs(prev => {
        const next = { ...prev };
        delete next[dirPath];
        // Also collapse any children
        for (const key of Object.keys(next)) {
          if (key.startsWith(dirPath + '/')) {
            delete next[key];
          }
        }
        return next;
      });
      return;
    }

    // Expand: fetch children
    setLoadingDirs(prev => new Set(prev).add(dirPath));
    try {
      const contents = await notebookService.listFiles(dirPath);
      const children = parseContents(contents);
      setExpandedDirs(prev => ({ ...prev, [dirPath]: children }));
    } catch (err) {
      console.error('Failed to load directory:', err);
      setError(`Failed to load ${dirPath}`);
    } finally {
      setLoadingDirs(prev => {
        const next = new Set(prev);
        next.delete(dirPath);
        return next;
      });
    }
  }, [expandedDirs, notebookService]);

  // Auto-expand my_data/ and samples/ on initial load
  useEffect(() => {
    if (autoExpandedRef.current || files.length === 0 || currentPath !== '') return;
    autoExpandedRef.current = true;

    const dirsToExpand = files.filter(
      f => f.type === 'directory' && (f.name === 'my_data' || f.name === 'samples')
    );
    dirsToExpand.forEach(dir => toggleDir(dir.path));
  }, [files, currentPath, toggleDir]);

  // Keyboard navigation: arrow keys traverse tree, Enter opens, F2 renames, Delete deletes, Escape clears
  useEffect(() => {
    const container = fileListRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle when the file list or its children are focused
      if (!container.contains(document.activeElement) && document.activeElement !== container) return;

      const flat = getFlatVisiblePaths();
      const currentIdx = selectedFile ? flat.indexOf(selectedFile) : -1;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const nextIdx = Math.min(currentIdx + 1, flat.length - 1);
          if (nextIdx >= 0) {
            setSelectedFile(flat[nextIdx]);
            setSelectedFiles(new Set());
            // Scroll the selected item into view
            const el = container.querySelector(`[data-path="${CSS.escape(flat[nextIdx])}"]`);
            el?.scrollIntoView({ block: 'nearest' });
          }
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prevIdx = Math.max(currentIdx - 1, 0);
          if (prevIdx >= 0 && flat.length > 0) {
            setSelectedFile(flat[prevIdx]);
            setSelectedFiles(new Set());
            const el = container.querySelector(`[data-path="${CSS.escape(flat[prevIdx])}"]`);
            el?.scrollIntoView({ block: 'nearest' });
          }
          break;
        }
        case 'Enter': {
          if (!selectedFile) break;
          const file = findFileByPath(selectedFile);
          if (file) {
            if (file.type === 'directory') {
              toggleDir(file.path);
            } else {
              onFileSelect?.(file);
            }
          }
          break;
        }
        case 'F2': {
          e.preventDefault();
          if (selectedFile && !isReadOnlyPath(selectedFile)) {
            const file = findFileByPath(selectedFile);
            if (file) {
              renameInitTimestamp.current = Date.now();
              setRenamingFile(file.path);
              setRenameValue(file.name);
            }
          }
          break;
        }
        case 'Delete':
        case 'Backspace': {
          if (selectedFile && !isReadOnlyPath(selectedFile) && !renamingFile) {
            setDeleteConfirmFile(selectedFile);
          }
          break;
        }
        case 'Escape': {
          if (renamingFile) {
            setRenamingFile(null);
          } else if (contextMenu) {
            setContextMenu(null);
          } else {
            setSelectedFile(null);
            setSelectedFiles(new Set());
          }
          break;
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }); // Intentionally no deps — reads from state via closure

  // Helper to find a FileItem by path from files + expandedDirs
  const findFileByPath = useCallback((path: string): FileItem | undefined => {
    const search = (items: FileItem[]): FileItem | undefined => {
      for (const item of items) {
        if (item.path === path) return item;
        if (item.type === 'directory' && expandedDirs[item.path]) {
          const found = search(expandedDirs[item.path]);
          if (found) return found;
        }
      }
      return undefined;
    };
    return search(files);
  }, [files, expandedDirs]);

  // Flatten visible file paths for shift-range selection
  const getFlatVisiblePaths = useCallback((): string[] => {
    const result: string[] = [];
    const walk = (items: FileItem[]) => {
      for (const item of items) {
        result.push(item.path);
        if (item.type === 'directory' && expandedDirs[item.path]) {
          walk(expandedDirs[item.path]);
        }
      }
    };
    walk(files);
    return result;
  }, [files, expandedDirs]);

  // Single-click: dirs toggle, files select. Ctrl/Cmd+Click toggles multi-select, Shift+Click for range.
  const handleFileClick = (file: FileItem, e?: React.MouseEvent) => {
    const isMultiToggle = e && (e.ctrlKey || e.metaKey);
    const isRangeSelect = e?.shiftKey;

    if (isMultiToggle) {
      // Toggle this file in/out of selection
      setSelectedFiles(prev => {
        const next = new Set(prev);
        if (next.has(file.path)) {
          next.delete(file.path);
        } else {
          next.add(file.path);
        }
        return next;
      });
      setSelectedFile(file.path);
      lastClickedPathRef.current = file.path;
      return;
    }

    if (isRangeSelect && lastClickedPathRef.current) {
      // Select range from last clicked to current
      const flat = getFlatVisiblePaths();
      const startIdx = flat.indexOf(lastClickedPathRef.current);
      const endIdx = flat.indexOf(file.path);
      if (startIdx !== -1 && endIdx !== -1) {
        const lo = Math.min(startIdx, endIdx);
        const hi = Math.max(startIdx, endIdx);
        setSelectedFiles(new Set(flat.slice(lo, hi + 1)));
        setSelectedFile(file.path);
        return;
      }
    }

    // Normal single click — clear multi-select
    setSelectedFiles(new Set());
    setSelectedFile(file.path);
    lastClickedPathRef.current = file.path;

    if (file.type === 'directory') {
      toggleDir(file.path);
    } else {
      trackRecentFile(file);
      onFileSelect?.(file);
    }
  };

  // Double-click: dirs navigate, files same as single-click
  const handleFileDoubleClick = (file: FileItem) => {
    if (file.type === 'directory') {
      navigateTo(file.path);
    } else {
      setSelectedFile(file.path);
      onFileSelect?.(file);
    }
  };

  const handleCreateItem = async () => {
    if (!newItemName) return;

    const targetDir = getTargetDir();

    if (isReadOnlyPath(targetDir)) {
      setError('The samples directory is read-only');
      return;
    }

    const fullPath = targetDir ? `${targetDir}/${newItemName}` : newItemName;

    try {
      if (newItemType === 'notebook') {
        const notebookPath = newItemName.endsWith('.ipynb') ? fullPath : fullPath + '.ipynb';
        await notebookService.createNotebook(notebookPath);
      } else if (newItemType === 'directory') {
        await notebookService.createDirectory(fullPath);
      } else {
        // If no extension, default to .ipynb; if .ipynb, create as notebook
        let filePath = fullPath;
        const hasExtension = newItemName.includes('.');
        if (!hasExtension) {
          filePath = fullPath + '.ipynb';
        }
        if (filePath.endsWith('.ipynb')) {
          await notebookService.createNotebook(filePath);
        } else {
          await notebookService.createFile(filePath);
        }
      }

      onFileCreate?.(fullPath, newItemType);
      refreshDirectory(fullPath);
      setShowNewItemDialog(false);
      setNewItemName('');
    } catch (err) {
      console.error('Failed to create item:', err);
      setError(`Failed to create ${newItemType}`);
    }
  };

  const uploadSingleFile = useCallback(async (file: File, targetPath: string): Promise<void> => {
    const fileContent = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });

    const filePath = targetPath ? `${targetPath}/${file.name}` : file.name;
    const isNotebook = file.name.endsWith('.ipynb');

    let body: any;
    if (isNotebook) {
      const nbContent = JSON.parse(fileContent);
      body = { type: 'notebook', format: 'json', content: nbContent };
    } else {
      body = { type: 'file', format: 'text', content: fileContent };
    }

    const authToken = localStorage.getItem('authToken');
    const resp = await fetch(
      `${getApiUrl()}/api/v1/notebooks/proxy/${sessionId}/api/contents/${filePath}?token=${token}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
      }
    );

    if (!resp.ok) {
      throw new Error(`Upload failed: ${resp.status}`);
    }
  }, [sessionId, token]);

  const handleMultiFileUpload = useCallback(async (fileList: FileList, targetPath: string) => {
    const filesToUpload = Array.from(fileList);
    if (filesToUpload.length === 0) return;

    setUploadLoading(true);
    setError(null);
    setUploadQueue(filesToUpload.map(f => ({ name: f.name, status: 'pending' })));

    let lastNotebook: { name: string; path: string } | null = null;

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      setUploadQueue(prev => prev.map((q, idx) =>
        idx === i ? { ...q, status: 'uploading' } : q
      ));

      try {
        await uploadSingleFile(file, targetPath);
        const filePath = targetPath ? `${targetPath}/${file.name}` : file.name;
        if (file.name.endsWith('.ipynb')) {
          lastNotebook = { name: file.name, path: filePath };
        }
        setUploadQueue(prev => prev.map((q, idx) =>
          idx === i ? { ...q, status: 'done' } : q
        ));
      } catch (err: any) {
        console.error(`Upload failed for ${file.name}:`, err);
        setUploadQueue(prev => prev.map((q, idx) =>
          idx === i ? { ...q, status: 'error', error: err.message } : q
        ));
      }
    }

    handleRefresh();

    if (lastNotebook && onFileSelect) {
      onFileSelect({ name: lastNotebook.name, path: lastNotebook.path, type: 'notebook' });
    }

    setUploadLoading(false);
    // Clear progress after a delay so user can see results
    setTimeout(() => setUploadQueue([]), 3000);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [uploadSingleFile, handleRefresh, onFileSelect]);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await handleMultiFileUpload(files, currentPath);
  }, [currentPath, handleMultiFileUpload]);

  const handleGitClone = useCallback(async () => {
    if (!gitRepoUrl) return;
    setGitCloneLoading(true);
    setGitCloneError(null);

    try {
      const authToken = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiUrl()}/api/v1/notebooks/git-clone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          repo_url: gitRepoUrl,
          target_dir: gitTargetDir || undefined,
        }),
      });

      if (!resp.ok) {
        let message = `Clone failed (${resp.status})`;
        try {
          const err = await resp.json();
          if (err.detail) message = err.detail;
        } catch {
          // non-JSON response
        }
        throw new Error(message);
      }

      setShowGitCloneDialog(false);
      setGitRepoUrl('');
      setGitTargetDir('');
      gitTargetDirManualRef.current = false;
      handleRefresh();
    } catch (err: any) {
      console.error('Git clone failed:', err);
      setGitCloneError(err.message || 'Clone failed');
    } finally {
      setGitCloneLoading(false);
    }
  }, [sessionId, gitRepoUrl, gitTargetDir, handleRefresh]);

  const getFileIcon = (type: FileItem['type'], name: string, isExpanded?: boolean) => {
    if (type === 'directory') {
      if (isExpanded) {
        // Open folder icon
        return (
          <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1H8a3 3 0 00-3 3v1.5a1.5 1.5 0 01-3 0V6z" clipRule="evenodd" />
            <path d="M6 12a2 2 0 012-2h8a2 2 0 012 2v2a2 2 0 01-2 2H2h2a2 2 0 002-2v-2z" />
          </svg>
        );
      }
      return (
        <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
        </svg>
      );
    } else if (type === 'notebook' || name.endsWith('.ipynb')) {
      return (
        <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    } else if (name.endsWith('.py')) {
      return (
        <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Close context menu on outside mousedown or scroll.
  // Using mousedown (not click) because ctrl+click on Mac fires both
  // contextmenu and click — with click, the close listener races with the
  // open and the menu never appears.
  useEffect(() => {
    if (!contextMenu) return;
    const handleClose = () => setContextMenu(null);
    document.addEventListener('mousedown', handleClose);
    document.addEventListener('scroll', handleClose, true);
    return () => {
      document.removeEventListener('mousedown', handleClose);
      document.removeEventListener('scroll', handleClose, true);
    };
  }, [contextMenu]);

  // Focus rename input when rename mode activates
  useEffect(() => {
    if (renamingFile && renameInputRef.current) {
      // Small delay to ensure the input is mounted and painted before focusing
      requestAnimationFrame(() => {
        if (!renameInputRef.current) return;
        renameInputRef.current.focus();
        const val = renameInputRef.current.value;
        const dotIndex = val.lastIndexOf('.');
        if (dotIndex > 0) {
          renameInputRef.current.setSelectionRange(0, dotIndex);
        } else {
          renameInputRef.current.select();
        }
      });
    }
  }, [renamingFile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Find a file across root files and all expanded dirs
  const findFile = useCallback((path: string): FileItem | undefined => {
    const found = files.find(f => f.path === path);
    if (found) return found;
    for (const children of Object.values(expandedDirs)) {
      const child = children.find(f => f.path === path);
      if (child) return child;
    }
    return undefined;
  }, [files, expandedDirs]);

  // Compute target directory based on current selection
  const getTargetDir = useCallback((): string => {
    if (selectedFile) {
      const file = findFile(selectedFile);
      if (file) {
        if (file.type === 'directory') return file.path;
        // Parent directory of a file
        const lastSlash = file.path.lastIndexOf('/');
        return lastSlash > 0 ? file.path.substring(0, lastSlash) : currentPath;
      }
    }
    return currentPath;
  }, [selectedFile, findFile, currentPath]);

  // Keyboard shortcuts: Delete and F2 — only when file explorer has focus
  const explorerContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedFile || renamingFile || showNewItemDialog || showGitCloneDialog) return;
      // Only fire when focus is inside the file explorer (not in a code cell, search bar, etc.)
      if (!explorerContainerRef.current?.contains(e.target as Node)) return;
      const file = findFile(selectedFile);
      if (!file) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (isReadOnlyPath(file.path)) {
          setError('The samples directory is read-only');
          return;
        }
        setDeleteConfirmFile(file.path);
      } else if (e.key === 'F2') {
        e.preventDefault();
        if (isReadOnlyPath(file.path)) {
          setError('The samples directory is read-only');
          return;
        }
        renameInitTimestamp.current = Date.now();
        setRenamingFile(file.path);
        setRenameValue(file.name);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedFile, findFile, renamingFile, showNewItemDialog, showGitCloneDialog]);

  const handleContextMenu = useCallback((e: React.MouseEvent, file: FileItem) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedFile(file.path);
    // Clamp position to keep menu within viewport
    const MENU_WIDTH = 200;
    const MENU_HEIGHT = 400;
    const x = Math.min(e.clientX, window.innerWidth - MENU_WIDTH - 8);
    const y = Math.min(e.clientY, window.innerHeight - MENU_HEIGHT - 8);
    setContextMenu({ x, y, file });
  }, []);

  const handleRename = async (file: FileItem) => {
    setContextMenu(null);
    if (isReadOnlyPath(file.path)) {
      setError('The samples directory is read-only');
      return;
    }
    renameInitTimestamp.current = Date.now();
    setRenamingFile(file.path);
    setRenameValue(file.name);
  };

  const handleRenameSubmit = async (file: FileItem) => {
    if (!renameValue.trim() || renameValue === file.name) {
      setRenamingFile(null);
      return;
    }
    try {
      const dir = file.path.substring(0, file.path.lastIndexOf('/') + 1);
      const newPath = dir + renameValue.trim();
      await notebookService.renameFile(file.path, newPath);
      setRenamingFile(null);
      refreshDirectory(file.path);
    } catch (err) {
      console.error('Rename failed:', err);
      setError('Failed to rename file');
      setRenamingFile(null);
    }
  };

  const handleDelete = async (file: FileItem) => {
    if (isReadOnlyPath(file.path)) {
      setError('The samples directory is read-only');
      setDeleteConfirmFile(null);
      return;
    }
    setDeleteConfirmFile(null);
    if (selectedFile === file.path) setSelectedFile(null);

    // Optimistic: remove from UI immediately
    const parentDir = file.path.substring(0, file.path.lastIndexOf('/'));
    const rollbackFiles = [...files];
    const rollbackExpanded = { ...expandedDirs };

    if (parentDir && expandedDirs[parentDir]) {
      setExpandedDirs(prev => ({
        ...prev,
        [parentDir]: prev[parentDir].filter(f => f.path !== file.path),
      }));
    } else {
      setFiles(prev => prev.filter(f => f.path !== file.path));
    }

    try {
      await notebookService.deleteFile(file.path);
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete file');
      // Rollback on failure
      setFiles(rollbackFiles);
      setExpandedDirs(rollbackExpanded);
    }
  };

  const handleBulkDelete = async () => {
    const paths = Array.from(selectedFiles).filter(p => !isReadOnlyPath(p));
    if (paths.length === 0) return;
    try {
      for (const path of paths) {
        await notebookService.deleteFile(path);
      }
      setSelectedFiles(new Set());
      setSelectedFile(null);
      setBulkDeleteConfirm(false);
      handleRefresh();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      setError('Failed to delete some files');
      setBulkDeleteConfirm(false);
      handleRefresh();
    }
  };

  const handleCopyPath = (file: FileItem) => {
    navigator.clipboard.writeText(file.path);
    setContextMenu(null);
  };

  const handleDuplicate = async (file: FileItem) => {
    setContextMenu(null);
    try {
      await notebookService.duplicateFile(file.path);
      refreshDirectory(file.path);
    } catch (err) {
      console.error('Duplicate failed:', err);
      setError('Failed to duplicate file');
    }
  };

  const handleDownload = async (file: FileItem) => {
    if (contextMenu) setContextMenu(null);
    setDownloadLoading(true);
    try {
      const model = await notebookService.getFileContent(file.path);
      let blob: Blob;
      if (model.type === 'notebook') {
        blob = new Blob([JSON.stringify(model.content, null, 2)], { type: 'application/json' });
      } else {
        blob = new Blob([model.content], { type: 'text/plain' });
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download file');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, file: FileItem) => {
    setDraggedFile(file);
    e.dataTransfer.effectAllowed = isReadOnlyPath(file.path) ? 'copy' : 'copyMove';
    e.dataTransfer.setData('text/plain', file.path);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedFile(null);
    dropTargetRef.current = null;
    setDropTargetDir(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, targetPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    // External file drop from desktop targeting a specific directory
    if (!draggedFile && e.dataTransfer.types.includes('Files')) {
      if (isReadOnlyPath(targetPath)) {
        e.dataTransfer.dropEffect = 'none';
        return;
      }
      e.dataTransfer.dropEffect = 'copy';
      // Only setState when target actually changes (eliminates hundreds of renders during drag)
      if (dropTargetRef.current !== targetPath) {
        dropTargetRef.current = targetPath;
        setDropTargetDir(targetPath);
      }
      return;
    }
    if (!draggedFile) return;
    // Can't drop on self, into own descendant, or into samples
    if (
      draggedFile.path === targetPath ||
      targetPath.startsWith(draggedFile.path + '/') ||
      isReadOnlyPath(targetPath)
    ) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = draggedFile && isReadOnlyPath(draggedFile.path) ? 'copy' : 'move';
    if (dropTargetRef.current !== targetPath) {
      dropTargetRef.current = targetPath;
      setDropTargetDir(targetPath);
    }
  }, [draggedFile]);

  const handleDragLeave = useCallback((e: React.DragEvent, targetPath: string) => {
    e.stopPropagation();
    // Only clear if we're leaving this specific target (not entering a child)
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (relatedTarget && (e.currentTarget as HTMLElement).contains(relatedTarget)) return;
    if (dropTargetRef.current === targetPath) {
      dropTargetRef.current = null;
      setDropTargetDir(null);
    }
  }, []);

  const handleDrop = async (e: React.DragEvent, targetDirPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    dropTargetRef.current = null;
    setDropTargetDir(null);

    // External file drop from desktop into a specific directory
    if (!draggedFile && e.dataTransfer.files.length > 0) {
      if (!isReadOnlyPath(targetDirPath)) {
        handleMultiFileUpload(e.dataTransfer.files, targetDirPath);
      }
      return;
    }

    if (!draggedFile) return;
    // Guard: can't drop onto self, into own descendant, or into samples
    if (
      draggedFile.path === targetDirPath ||
      targetDirPath.startsWith(draggedFile.path + '/') ||
      isReadOnlyPath(targetDirPath)
    ) {
      setDraggedFile(null);
      return;
    }

    const newPath = targetDirPath ? `${targetDirPath}/${draggedFile.name}` : draggedFile.name;

    // Don't move if already in this directory
    if (newPath === draggedFile.path) {
      setDraggedFile(null);
      return;
    }

    try {
      if (isReadOnlyPath(draggedFile.path)) {
        // Copy from samples — file stays in place
        await notebookService.copyFileTo(draggedFile.path, targetDirPath);
      } else {
        await notebookService.renameFile(draggedFile.path, newPath);
      }
      handleRefresh();
    } catch (err) {
      console.error(isReadOnlyPath(draggedFile.path) ? 'Copy failed:' : 'Move failed:', err);
      setError(isReadOnlyPath(draggedFile.path) ? 'Failed to copy file' : 'Failed to move file');
    } finally {
      setDraggedFile(null);
    }
  };

  // Filter files by search query — matches name substring, case-insensitive
  const filterFiles = (items: FileItem[], query: string): FileItem[] => {
    if (!query) return items;
    const lowerQ = query.toLowerCase();
    return items.filter(f => {
      if (f.name.toLowerCase().includes(lowerQ)) return true;
      // For expanded dirs, check if any child matches
      if (f.type === 'directory' && expandedDirs[f.path]) {
        return filterFiles(expandedDirs[f.path], query).length > 0;
      }
      return false;
    });
  };

  const DIR_RENDER_CHUNK = 50;

  // Recursive tree renderer with item limits for large directories
  const renderFileTree = (items: FileItem[], depth: number, parentPath?: string) => {
    const limit = parentPath ? (dirRenderLimits[parentPath] ?? DIR_RENDER_CHUNK) : items.length;
    const visible = items.length > limit ? items.slice(0, limit) : items;
    const hiddenCount = items.length - visible.length;

    const rows = visible.map(file => {
      const isDir = file.type === 'directory';
      const isExpanded = expandedDirs[file.path] !== undefined;
      const isDirLoading = loadingDirs.has(file.path);
      const children = expandedDirs[file.path];
      const isDragged = draggedFile?.path === file.path;
      const isDropTarget = isDir && dropTargetDir === file.path;
      const isInvalidDropTarget = !!(isDir && draggedFile && (
        draggedFile.path === file.path ||
        file.path.startsWith(draggedFile.path + '/') ||
        isReadOnlyPath(file.path)
      ));

      return (
        <div key={file.path}>
          <FileTreeRow
            file={file}
            depth={depth}
            isSelected={selectedFile === file.path}
            isMultiSelected={selectedFiles.has(file.path)}
            isExpanded={isExpanded}
            isDirLoading={isDirLoading}
            isDragged={isDragged}
            isDropTarget={isDropTarget}
            isInvalidDropTarget={isInvalidDropTarget}
            isRenaming={renamingFile === file.path}
            renameValue={renameValue}
            deleteConfirm={deleteConfirmFile === file.path}
            searchQuery={debouncedQuery || undefined}
            onFileClick={handleFileClick}
            onDoubleClick={handleFileDoubleClick}
            onContextMenu={handleContextMenu}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={isDir ? handleDragOver : undefined}
            onDragLeave={isDir ? handleDragLeave : undefined}
            onDrop={isDir ? handleDrop : undefined}
            onRenameChange={setRenameValue}
            onRenameSubmit={handleRenameSubmit}
            onRenameCancel={() => setRenamingFile(null)}
            onDeleteConfirm={handleDelete}
            onDeleteCancel={() => setDeleteConfirmFile(null)}
            renameInitTimestamp={renameInitTimestamp.current}
          />

          {/* Render children if expanded */}
          {isExpanded && children && children.length > 0 && renderFileTree(children, depth + 1, file.path)}
          {isExpanded && children && children.length === 0 && (
            <div
              className="text-xs text-gray-600 italic py-1"
              style={{ paddingLeft: `${28 + depth * 16}px` }}
            >
              Empty
            </div>
          )}
        </div>
      );
    });

    if (hiddenCount > 0 && parentPath) {
      rows.push(
        <button
          key={`__show-more-${parentPath}`}
          onClick={() => setDirRenderLimits(prev => ({
            ...prev,
            [parentPath]: (prev[parentPath] ?? DIR_RENDER_CHUNK) + DIR_RENDER_CHUNK,
          }))}
          className="w-full py-1 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/5 transition-colors text-center"
          style={{ paddingLeft: `${12 + (depth) * 16}px` }}
        >
          Show {Math.min(hiddenCount, DIR_RENDER_CHUNK)} more ({hiddenCount} remaining)
        </button>
      );
    }

    return rows;
  };

  // Parse current path for breadcrumbs
  const pathParts = currentPath ? currentPath.split('/').filter(Boolean) : [];

  return (
    <div ref={explorerContainerRef} className={`flex flex-col h-full bg-black/50 backdrop-blur-sm border border-gray-800/50 ${className}`}>
      {/* Header with navigation and actions */}
      <div className="flex-shrink-0 border-b border-gray-800/50">
        {/* Breadcrumb navigation */}
        <div className="flex items-center px-3 py-2 pr-10 bg-black/30 backdrop-blur-sm">
          <button
            onClick={navigateBack}
            disabled={pathHistory.length === 0}
            className={`mr-2 p-1.5 transition-all flex-shrink-0 ${
              pathHistory.length > 0
                ? 'text-gray-400 hover:text-white hover:bg-white/10'
                : 'text-gray-600 cursor-not-allowed'
            }`}
            title="Back"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex items-center text-sm min-w-0 overflow-hidden">
            <button
              onClick={navigateToRoot}
              className="text-gray-400 hover:text-white flex-shrink-0"
            >
              Home
            </button>
            {pathParts.map((part, index) => (
              <React.Fragment key={index}>
                <span className="mx-1 text-gray-600 flex-shrink-0">/</span>
                <button
                  onClick={() => {
                    const targetPath = pathParts.slice(0, index + 1).join('/');
                    navigateTo(targetPath);
                  }}
                  className="text-gray-400 hover:text-white truncate"
                >
                  {part}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Search bar */}
        <div className="px-3 py-1.5 bg-black/20 border-t border-gray-800/30">
          <div className="relative">
            <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Filter files..."
              className="w-full pl-7 pr-6 py-1 text-xs bg-gray-900/50 border border-gray-700/50 text-gray-300 placeholder-gray-600 focus:border-blue-500/50 focus:outline-none transition-colors"
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setDebouncedQuery(''); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Sort selector */}
        <div className="flex items-center px-3 py-1 bg-black/20 border-t border-gray-800/30">
          <span className="text-[10px] text-gray-500 mr-1.5">Sort:</span>
          {(['name', 'date', 'size', 'type'] as SortMode[]).map(mode => (
            <button
              key={mode}
              onClick={() => setSortMode(mode)}
              className={`text-[10px] px-1.5 py-0.5 transition-colors ${
                sortMode === mode
                  ? 'text-blue-400 bg-blue-500/10'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Action toolbar */}
        <div className="flex items-center justify-end px-3 py-1 bg-black/20 border-t border-gray-800/30 space-x-1">
          <button
            onClick={handleRefresh}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 transition-all"
            title="Refresh"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          <button
            onClick={() => setShowNewItemDialog(true)}
            disabled={isReadOnlyPath(getTargetDir())}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:text-gray-600 disabled:cursor-not-allowed"
            title={isReadOnlyPath(getTargetDir()) ? 'Cannot create in samples (read-only)' : 'New item'}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadLoading || isReadOnlyPath(getTargetDir())}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:text-gray-600 disabled:cursor-not-allowed"
            title={isReadOnlyPath(getTargetDir()) ? 'Cannot upload to samples (read-only)' : 'Upload file'}
          >
            {uploadLoading ? (
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".ipynb,.py,.md,.txt,.csv,.json,.yaml,.yml,.toml,.r,.R,.jl,.sh"
            className="hidden"
            onChange={handleFileUpload}
          />

          <button
            onClick={() => {
              const file = selectedFile ? findFile(selectedFile) : undefined;
              if (file && file.type !== 'directory') {
                handleDownload(file);
              }
            }}
            disabled={downloadLoading || !selectedFile || findFile(selectedFile ?? '')?.type === 'directory'}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:text-gray-600 disabled:cursor-not-allowed"
            title={selectedFile ? `Download ${findFile(selectedFile)?.name ?? 'file'}` : 'Select a file to download'}
          >
            {downloadLoading ? (
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
          </button>

          <button
            onClick={() => {
              if (selectedFile) setDeleteConfirmFile(selectedFile);
            }}
            disabled={!selectedFile || isReadOnlyPath(selectedFile)}
            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-white/10 transition-all disabled:text-gray-600 disabled:cursor-not-allowed"
            title={!selectedFile ? 'Select a file to delete' : isReadOnlyPath(selectedFile) ? 'Cannot delete samples (read-only)' : `Delete ${findFile(selectedFile)?.name ?? 'item'}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>

          <button
            onClick={() => {
              setShowGitCloneDialog(true);
              setGitCloneError(null);
              gitTargetDirManualRef.current = false;
            }}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 transition-all"
            title="Clone git repository"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="18" r="3" />
              <circle cx="6" cy="6" r="3" />
              <circle cx="18" cy="6" r="3" />
              <path d="M18 9v1a2 2 0 01-2 2H8a2 2 0 01-2-2V9" />
              <line x1="12" y1="12" x2="12" y2="15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Bulk selection bar */}
      {selectedFiles.size > 1 && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-900/20 border-b border-blue-800/30 flex-shrink-0">
          <span className="text-xs text-blue-300 font-medium">{selectedFiles.size} selected</span>
          <div className="flex-1" />
          {!bulkDeleteConfirm ? (
            <>
              <button
                onClick={() => setBulkDeleteConfirm(true)}
                disabled={Array.from(selectedFiles).every(p => isReadOnlyPath(p))}
                className="px-2 py-0.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors disabled:opacity-30"
              >
                Delete
              </button>
              <button
                onClick={() => { setSelectedFiles(new Set()); setBulkDeleteConfirm(false); }}
                className="px-2 py-0.5 text-xs text-gray-400 hover:text-white transition-colors"
              >
                Clear
              </button>
            </>
          ) : (
            <>
              <span className="text-xs text-red-300">Delete {Array.from(selectedFiles).filter(p => !isReadOnlyPath(p)).length} items?</span>
              <button
                onClick={handleBulkDelete}
                className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-500 transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => setBulkDeleteConfirm(false)}
                className="px-2 py-0.5 text-xs text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}

      {/* Upload progress */}
      {uploadQueue.length > 0 && (
        <div className="flex-shrink-0 border-b border-gray-800/30 px-3 py-1.5 bg-black/20 space-y-0.5 max-h-28 overflow-y-auto">
          {uploadQueue.map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              {item.status === 'uploading' ? (
                <svg className="w-3 h-3 animate-spin text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              ) : item.status === 'done' ? (
                <svg className="w-3 h-3 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : item.status === 'error' ? (
                <svg className="w-3 h-3 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <span className="w-3 h-3 rounded-full border border-gray-600 flex-shrink-0" />
              )}
              <span className={`truncate ${
                item.status === 'done' ? 'text-gray-500' :
                item.status === 'error' ? 'text-red-400' :
                item.status === 'uploading' ? 'text-blue-300' :
                'text-gray-400'
              }`}>
                {item.name}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* File tree — also a drop target for the current directory and external file drops */}
      <div
        className={`flex-1 overflow-y-auto ${
          dropTargetDir === currentPath && (draggedFile || dropTargetDir === '__external__')
            ? 'ring-1 ring-inset ring-blue-500/50 bg-blue-500/5'
            : ''
        }`}
        ref={fileListRef}
        tabIndex={0}
        role="tree"
        aria-label="File explorer"
        onDragOver={(e) => {
          // External file drop from desktop
          if (!draggedFile && e.dataTransfer.types.includes('Files') && !isReadOnlyPath(currentPath)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            setDropTargetDir(currentPath);
            return;
          }
          // Internal drag-and-drop
          if (draggedFile && !isReadOnlyPath(currentPath)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = isReadOnlyPath(draggedFile.path) ? 'copy' : 'move';
            setDropTargetDir(currentPath);
          }
        }}
        onDragLeave={(e) => {
          const relatedTarget = e.relatedTarget as HTMLElement | null;
          if (relatedTarget && (e.currentTarget as HTMLElement).contains(relatedTarget)) return;
          if (dropTargetDir === currentPath) setDropTargetDir(null);
        }}
        onDrop={(e) => {
          // External file drop from desktop
          if (!draggedFile && e.dataTransfer.files.length > 0 && !isReadOnlyPath(currentPath)) {
            e.preventDefault();
            e.stopPropagation();
            setDropTargetDir(null);
            handleMultiFileUpload(e.dataTransfer.files, currentPath);
            return;
          }
          // Internal drag-and-drop
          handleDrop(e, currentPath);
        }}
      >
        {/* Recently opened files */}
        {recentFiles.length > 0 && !debouncedQuery && (
          <div className="border-b border-gray-800/30">
            <button
              onClick={() => setRecentCollapsed(prev => !prev)}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors"
            >
              <svg
                className={`w-2.5 h-2.5 transition-transform duration-150 ${recentCollapsed ? '' : 'rotate-90'}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Recent
            </button>
            {!recentCollapsed && (
              <div className="pb-1">
                {recentFiles.map(recent => (
                  <button
                    key={recent.path}
                    onClick={() => {
                      const file: FileItem = {
                        name: recent.name,
                        path: recent.path,
                        type: recent.name.endsWith('.ipynb') ? 'notebook' : 'file',
                      };
                      setSelectedFile(recent.path);
                      trackRecentFile(file);
                      onFileSelect?.(file);
                    }}
                    className={`w-full flex items-center py-1 text-left text-sm transition-colors border-l-2 ${
                      selectedFile === recent.path
                        ? 'bg-blue-500/10 text-white border-blue-500'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white border-transparent'
                    }`}
                    style={{ paddingLeft: '16px', paddingRight: '12px' }}
                    title={recent.path}
                  >
                    <span className="mr-2 flex-shrink-0">
                      {getFileIcon(recent.name.endsWith('.ipynb') ? 'notebook' : 'file', recent.name)}
                    </span>
                    <span className="truncate">{recent.name}</span>
                    <span className="ml-auto text-[10px] text-gray-600 flex-shrink-0 pl-2">
                      {recent.path.includes('/') ? recent.path.substring(0, recent.path.lastIndexOf('/')) : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-500 text-sm">Loading...</div>
          </div>
        ) : error ? (
          <div className="p-4">
            <div className="flex items-start justify-between text-red-400 text-sm">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-2 text-red-400/60 hover:text-red-300 flex-shrink-0"
                title="Dismiss"
              >
                &times;
              </button>
            </div>
          </div>
        ) : files.length === 0 ? (
          <div className="p-4 text-center">
            <div className="text-gray-500 text-sm mb-3">Empty directory</div>
            <button
              onClick={() => setShowNewItemDialog(true)}
              className="px-4 py-1.5 text-sm bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 hover:text-blue-300 border border-blue-600/30 backdrop-blur-sm transition-all"
            >
              + Create your first file
            </button>
          </div>
        ) : (
          <div className="py-1">
            {renderFileTree(filterFiles(files, debouncedQuery), 0)}
          </div>
        )}
      </div>

      {/* Context menu — portaled to document.body to escape backdrop-blur containing block + overflow clipping */}
      {contextMenu && ReactDOM.createPortal(
        <div
          className="fixed z-[100] min-w-[160px] py-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => { handleFileDoubleClick(contextMenu.file); setContextMenu(null); }}
            className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Open
          </button>
          {isReadOnlyPath(contextMenu.file.path) && contextMenu.file.type !== 'directory' && (
            <button
              onClick={async () => {
                const file = contextMenu.file;
                setContextMenu(null);
                try {
                  // Wait for my_data/ rclone mount (short backoff — instance already running)
                  const BACKOFF = [500, 1000, 2000];
                  for (let i = 0; i < BACKOFF.length; i++) {
                    try {
                      await notebookService.listFiles('my_data');
                      break;
                    } catch {
                      if (i === BACKOFF.length - 1) throw new Error('my_data directory not available');
                      await new Promise(r => setTimeout(r, BACKOFF[i]));
                    }
                  }
                  const result = await notebookService.smartCopyFileTo(file.path, 'my_data');
                  handleRefresh();
                  if (onFileSelect && file.type === 'notebook') {
                    onFileSelect({ ...file, path: result.destPath });
                  }
                } catch (err) {
                  console.error('Copy to my_data failed:', err);
                  setError('Failed to copy to my_data');
                }
              }}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy to My Data
            </button>
          )}
          {/* Copy directory to my_data — for sample project dirs (not strategy_templates) */}
          {isReadOnlyPath(contextMenu.file.path) &&
           contextMenu.file.type === 'directory' &&
           contextMenu.file.path.startsWith('samples/') &&
           contextMenu.file.path !== 'samples/strategy_templates' &&
           !contextMenu.file.path.startsWith('samples/strategy_templates/') && (
            <button
              onClick={async () => {
                const dir = contextMenu.file;
                setContextMenu(null);
                try {
                  const BACKOFF = [500, 1000, 2000];
                  for (let i = 0; i < BACKOFF.length; i++) {
                    try {
                      await notebookService.listFiles('my_data');
                      break;
                    } catch {
                      if (i === BACKOFF.length - 1) throw new Error('my_data directory not available');
                      await new Promise(r => setTimeout(r, BACKOFF[i]));
                    }
                  }
                  const result = await notebookService.smartCopyDirectoryTo(dir.path, 'my_data');
                  handleRefresh();
                  if (result.failed.length > 0) {
                    console.warn('Partial directory copy failures:', result.failed);
                  }
                  if (result.skipped.length > 0) {
                    console.log('Skipped unchanged files:', result.skipped);
                  }
                } catch (err) {
                  console.error('Copy directory to my_data failed:', err);
                  setError('Failed to copy directory to my_data');
                }
              }}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy to My Data
            </button>
          )}
          {!isReadOnlyPath(contextMenu.file.path) && (
            <button
              onClick={() => handleRename(contextMenu.file)}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Rename
            </button>
          )}
          <button
            onClick={() => handleCopyPath(contextMenu.file)}
            className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
            </svg>
            Copy Path
          </button>
          {contextMenu.file.type !== 'directory' && !isReadOnlyPath(contextMenu.file.path) && (
            <button
              onClick={() => handleDuplicate(contextMenu.file)}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Duplicate
            </button>
          )}
          {contextMenu.file.type !== 'directory' && (
            <button
              onClick={() => handleDownload(contextMenu.file)}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
          )}
          {!isReadOnlyPath(contextMenu.file.path) && (
            <>
              <div className="border-t border-gray-700 my-1" />
              <button
                onClick={() => { setDeleteConfirmFile(contextMenu.file.path); setContextMenu(null); }}
                className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-red-900/30 hover:text-red-300 flex items-center gap-2"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </>
          )}
        </div>,
        document.body
      )}

      {/* New item dialog — portaled to escape containing block */}
      {showNewItemDialog && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black/90 backdrop-blur-md p-6 w-96 border border-gray-800/50 shadow-2xl">
            <h3 className="text-white font-semibold mb-4 text-lg">Create New Item</h3>

            <div className="mb-3 text-xs text-gray-500">
              Location: <span className="text-gray-400 font-mono">{getTargetDir() || '/'}</span>
            </div>

            <div className="mb-4">
              <label className="text-sm text-gray-400 block mb-2">Type</label>
              <select
                value={newItemType}
                onChange={(e) => setNewItemType(e.target.value as any)}
                className="w-full px-3 py-2 bg-black/50 text-white border border-gray-700/50 focus:border-blue-500 focus:outline-none backdrop-blur-sm transition-all"
              >
                <option value="notebook">Notebook</option>
                <option value="file">File</option>
                <option value="directory">Directory</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="text-sm text-gray-400 block mb-2">Name</label>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder={newItemType === 'notebook' ? 'my_notebook.ipynb' : 'name'}
                className="w-full px-3 py-2 bg-black/50 text-white border border-gray-700/50 focus:border-blue-500 focus:outline-none backdrop-blur-sm transition-all"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateItem();
                  if (e.key === 'Escape') {
                    setShowNewItemDialog(false);
                    setNewItemName('');
                  }
                }}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewItemDialog(false);
                  setNewItemName('');
                }}
                className="px-4 py-2 text-gray-400 hover:text-white bg-gray-800/30 hover:bg-gray-800/50 border border-gray-700/50 backdrop-blur-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateItem}
                disabled={!newItemName}
                className="px-4 py-2 bg-blue-600/80 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm transition-all shadow hover:shadow-blue-500/20"
              >
                Create
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Git clone dialog — portaled to escape containing block */}
      {showGitCloneDialog && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black/90 backdrop-blur-md p-6 w-96 border border-gray-800/50 shadow-2xl">
            <h3 className="text-white font-semibold mb-4 text-lg">Clone Git Repository</h3>

            <div className="mb-4">
              <label className="text-sm text-gray-400 block mb-2">Repository URL</label>
              <input
                type="text"
                value={gitRepoUrl}
                onChange={(e) => {
                  const url = e.target.value;
                  setGitRepoUrl(url);
                  if (!gitTargetDirManualRef.current && url.trim()) {
                    const name = url.trim().replace(/\/+$/, '').replace(/\.git$/, '').split('/').pop() || '';
                    setGitTargetDir(name);
                  }
                  if (!url.trim()) {
                    setGitTargetDir('');
                    gitTargetDirManualRef.current = false;
                  }
                }}
                placeholder="https://github.com/user/repo.git"
                className="w-full px-3 py-2 bg-black/50 text-white border border-gray-700/50 focus:border-blue-500 focus:outline-none backdrop-blur-sm transition-all font-mono text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && gitRepoUrl) handleGitClone();
                  if (e.key === 'Escape') {
                    setShowGitCloneDialog(false);
                    setGitRepoUrl('');
                    setGitTargetDir('');
                  }
                }}
              />
            </div>

            <div className="mb-6">
              <label className="text-sm text-gray-400 block mb-2">Directory name <span className="text-gray-600">(optional)</span></label>
              <input
                type="text"
                value={gitTargetDir}
                onChange={(e) => {
                  setGitTargetDir(e.target.value);
                  gitTargetDirManualRef.current = true;
                }}
                placeholder="auto-detected from URL"
                className="w-full px-3 py-2 bg-black/50 text-white border border-gray-700/50 focus:border-blue-500 focus:outline-none backdrop-blur-sm transition-all text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && gitRepoUrl) handleGitClone();
                  if (e.key === 'Escape') {
                    setShowGitCloneDialog(false);
                    setGitRepoUrl('');
                    setGitTargetDir('');
                  }
                }}
              />
            </div>

            <p className="text-xs text-gray-500 mb-4">Only HTTPS URLs are supported. The repo will be shallow-cloned into your workspace.</p>

            {gitCloneError && (
              <div className="mb-4 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {gitCloneError}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowGitCloneDialog(false);
                  setGitRepoUrl('');
                  setGitTargetDir('');
                  setGitCloneError(null);
                  gitTargetDirManualRef.current = false;
                }}
                className="px-4 py-2 text-gray-400 hover:text-white bg-gray-800/30 hover:bg-gray-800/50 border border-gray-700/50 backdrop-blur-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleGitClone}
                disabled={!gitRepoUrl || gitCloneLoading}
                className="px-4 py-2 bg-blue-600/80 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm transition-all shadow hover:shadow-blue-500/20 flex items-center space-x-2"
              >
                {gitCloneLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                      <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span>Cloning...</span>
                  </>
                ) : (
                  <span>Clone</span>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
